/**
 * Error Handler Hook for React Components
 * Provides consistent error handling and user notifications
 */

import { useCallback, useState } from 'react';
import {
  extractErrorInfo,
  logError,
  getNotificationMessage,
  shouldRetry,
  ProcessedError,
  Language
} from '../utils/errorUtils';

export interface UseErrorHandlerOptions {
  language?: Language;
  showToast?: boolean;
  autoRetry?: boolean;
  maxRetries?: number;
  retryDelay?: number;
  onError?: (error: ProcessedError) => void;
  onRetry?: (attempt: number) => void;
}

export interface ErrorState {
  error: ProcessedError | null;
  isRetrying: boolean;
  retryCount: number;
  hasError: boolean;
}

export interface ErrorHandlerMethods {
  handleError: (error: any, context?: Record<string, any>) => void;
  clearError: () => void;
  retry: () => Promise<void>;
  showError: (message: string, type?: string) => void;
}

export function useErrorHandler(
  options: UseErrorHandlerOptions = {}
): [ErrorState, ErrorHandlerMethods] {
  const {
    language = 'th',
    showToast = true,
    autoRetry = false,
    maxRetries = 3,
    retryDelay = 1000,
    onError,
    onRetry
  } = options;

  const [errorState, setErrorState] = useState<ErrorState>({
    error: null,
    isRetrying: false,
    retryCount: 0,
    hasError: false
  });

  const handleError = useCallback((error: any, context?: Record<string, any>) => {
    // Extract standardized error information
    const processedError = extractErrorInfo(error, {
      language,
      showDetails: process.env.NODE_ENV === 'development',
      showFieldErrors: true,
      showRequestId: true
    });

    // Log error for debugging
    logError(error, {
      ...context,
      component: 'useErrorHandler',
      user_action: context?.userAction || 'unknown'
    });

    // Update error state
    setErrorState(prev => ({
      ...prev,
      error: processedError,
      hasError: true,
      isRetrying: false
    }));

    // Call custom error handler
    if (onError) {
      onError(processedError);
    }

    // Show toast notification
    if (showToast) {
      showToastNotification(processedError, language);
    }

    // Auto-retry if enabled and error is retryable
    if (autoRetry && shouldRetry(error) && errorState.retryCount < maxRetries) {
      setTimeout(() => {
        retry();
      }, retryDelay * (errorState.retryCount + 1)); // Exponential backoff
    }
  }, [language, showToast, autoRetry, maxRetries, retryDelay, onError, errorState.retryCount]);

  const clearError = useCallback(() => {
    setErrorState({
      error: null,
      isRetrying: false,
      retryCount: 0,
      hasError: false
    });
  }, []);

  const retry = useCallback(async () => {
    if (!errorState.error) return;

    setErrorState(prev => ({
      ...prev,
      isRetrying: true,
      retryCount: prev.retryCount + 1
    }));

    if (onRetry) {
      onRetry(errorState.retryCount + 1);
    }

    // Note: The actual retry logic should be implemented by the calling component
    // This hook just manages the retry state
  }, [errorState.error, errorState.retryCount, onRetry]);

  const showError = useCallback((message: string, type: string = 'error') => {
    const customError: ProcessedError = {
      title: 'ข้อผิดพลาด',
      message,
      type: type as any,
      code: 'CUSTOM_ERROR' as any,
      isRetryable: false
    };

    setErrorState(prev => ({
      ...prev,
      error: customError,
      hasError: true,
      isRetrying: false
    }));

    if (showToast) {
      showToastNotification(customError, language);
    }
  }, [language, showToast]);

  return [
    errorState,
    {
      handleError,
      clearError,
      retry,
      showError
    }
  ];
}

// Toast notification function (you can replace this with your preferred toast library)
function showToastNotification(error: ProcessedError, language: Language) {
  // For now, just use console or alert
  // In a real app, you would integrate with react-toastify, react-hot-toast, etc.

  if (process.env.NODE_ENV === 'development') {
    console.error(`[${error.type}] ${error.title}: ${error.message}`);
    if (error.suggestedAction) {
      console.info(`Suggested action: ${error.suggestedAction}`);
    }
  }

  // You can implement actual toast notifications here
  // Example with react-toastify:
  // toast.error(error.message, {
  //   autoClose: error.isRetryable ? 5000 : 3000,
  //   closeOnClick: true,
  //   pauseOnHover: true
  // });
}

// Hook for form validation errors
export function useFormErrorHandler(language: Language = 'th') {
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [generalError, setGeneralError] = useState<string | null>(null);

  const handleFormError = useCallback((error: any) => {
    const processedError = extractErrorInfo(error, {
      language,
      showFieldErrors: true
    });

    // Clear previous errors
    setFieldErrors({});
    setGeneralError(null);

    // Handle field-level errors
    if (processedError.fieldErrors && processedError.fieldErrors.length > 0) {
      const errors: Record<string, string> = {};

      processedError.fieldErrors.forEach(fieldError => {
        const message = language === 'en' && fieldError.message_en
          ? fieldError.message_en
          : fieldError.message;
        errors[fieldError.field] = message;
      });

      setFieldErrors(errors);
    } else {
      // Set general error if no field-specific errors
      setGeneralError(processedError.message);
    }

    // Log error
    logError(error, { context: 'form_validation' });
  }, [language]);

  const clearFormErrors = useCallback(() => {
    setFieldErrors({});
    setGeneralError(null);
  }, []);

  const setFieldError = useCallback((field: string, message: string) => {
    setFieldErrors(prev => ({ ...prev, [field]: message }));
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setFieldErrors(prev => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });
  }, []);

  return {
    fieldErrors,
    generalError,
    handleFormError,
    clearFormErrors,
    setFieldError,
    clearFieldError,
    hasErrors: Object.keys(fieldErrors).length > 0 || !!generalError
  };
}

// Hook for async operations with error handling
export function useAsyncError(options: UseErrorHandlerOptions = {}) {
  const [errorState, errorMethods] = useErrorHandler(options);
  const [isLoading, setIsLoading] = useState(false);

  const executeAsync = useCallback(async <T>(
    asyncFn: () => Promise<T>,
    context?: Record<string, any>
  ): Promise<T | null> => {
    try {
      setIsLoading(true);
      errorMethods.clearError();

      const result = await asyncFn();
      return result;
    } catch (error) {
      errorMethods.handleError(error, context);
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [errorMethods]);

  const retryAsync = useCallback(async <T>(
    asyncFn: () => Promise<T>,
    context?: Record<string, any>
  ): Promise<T | null> => {
    if (!errorState.error?.isRetryable) {
      return null;
    }

    try {
      setIsLoading(true);
      await errorMethods.retry();

      const result = await asyncFn();
      errorMethods.clearError(); // Clear error on successful retry
      return result;
    } catch (error) {
      errorMethods.handleError(error, { ...context, isRetry: true });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, [errorState.error, errorMethods]);

  return {
    ...errorState,
    isLoading,
    executeAsync,
    retryAsync,
    ...errorMethods
  };
}

export default useErrorHandler;